import React from "react";

/**
 * Full-viewport overlay with a centered spinner.
 * Mount this anywhere in the tree while a mutation is in-flight —
 * it blocks all pointer interaction across the entire UI until dismissed.
 * Matches the FlowAccount UX pattern: nothing is clickable during save/submit.
 *
 * The `role`/`aria-busy`/`aria-label` below are not decoration. This element
 * covers the viewport and swallows every click, and until now it did so with no
 * className, no role and no accessible name — so a screen-reader user got
 * silence while the UI was frozen, and no test could see it either. Two
 * overlay defects have shipped here (Bug-32, and the Interview Hub gap); both
 * were found by hand because nothing could assert on this component.
 *
 * data-testid is the stable hook: the styles are inline and the markup has no
 * class to select on, so any test selector without it is guessing.
 */
export function LoadingOverlay({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div
      role="status"
      aria-busy="true"
      // No aria-live: role="status" already implies aria-live="polite", and
      // spelling both out is redundant rather than reinforcing.
      aria-label="Loading"
      data-testid="loading-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(255,255,255,0.65)",
        backdropFilter: "blur(1.5px)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
      <div style={{
        position: "relative", width: 52, height: 52,
      }}>
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "5px solid #e2e8f0",
          borderTopColor: "#1e3a8a",
          animation: "spin 0.75s linear infinite",
        }} />
        <img
          src="/logo-circle.png"
          alt="HRxAI"
          style={{
            position: "absolute", inset: 8,
            width: "calc(100% - 16px)", height: "calc(100% - 16px)",
            borderRadius: "50%", objectFit: "contain",
          }}
        />
      </div>
    </div>
  );
}
